import { useSidebarsWidthStore } from "@core-ui/ContextServices/Sidebars/SidebarsWidthStore";
import useLeftNavigationWidthVar from "@core-ui/hooks/useLeftNavigationWidthVar";
import { SIDEBAR_TRIGGER_ATTR } from "@core-ui/hooks/useSidebarFloating";
import t from "@ext/localization/locale/translate";
import {
	LEFT_NAV_DEFAULT_WIDTH,
	LEFT_NAV_MAX_WIDTH,
	LEFT_NAV_MIN_WIDTH,
} from "@ext/navigation/catalog/SidebarNavigation/utils/constants";
import { SidebarResizer, useSidebar } from "@ui-kit/Sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { useCallback } from "react";
import { VIEWPORT_PADDING } from "../../../../ui-kit/lib/floating";
import { COLLAPSED_PANEL_OFFSET, NAV_Z_INDEX_VALUE } from "./leftNavigationLayoutConstants";

const CollapsedEdgeHandle = () => (
	<Tooltip>
		<TooltipTrigger asChild>
			<div
				{...{ [SIDEBAR_TRIGGER_ATTR]: true }}
				className="h-full w-full"
				data-testid="collapsed-navigation-edge"
			/>
		</TooltipTrigger>
		<TooltipContent focus="high" side="right">
			{t("left-navigation.expand")}
		</TooltipContent>
	</Tooltip>
);

const collapsedEdge = (hasBottom: boolean) => ({
	handle: <CollapsedEdgeHandle />,
	handleInset: {
		top: `calc(var(--catalog-titlebar-offset,0rem) + ${COLLAPSED_PANEL_OFFSET + VIEWPORT_PADDING}px)`,
		bottom: (hasBottom ? COLLAPSED_PANEL_OFFSET : 0) + VIEWPORT_PADDING,
	},
	handleOffset: VIEWPORT_PADDING,
});

interface LeftNavigationResizerProps {
	isCollapsed: boolean;
	isVisible: boolean;
	hasBottom: boolean;
	onResize: (width: number) => void;
	onResizingChange: (isResizing: boolean) => void;
	onPointerEnter: () => void;
	onPointerLeave: () => void;
}

export const LeftNavigationResizer = ({
	isCollapsed,
	isVisible,
	hasBottom,
	onResize,
	onResizingChange,
	onPointerEnter,
	onPointerLeave,
}: LeftNavigationResizerProps) => {
	const width = useSidebarsWidthStore((state) => state.leftWidth);
	const setLeftWidth = useSidebarsWidthStore((state) => state.setLeftWidth);
	const setWidthVar = useLeftNavigationWidthVar();
	const { toggleSidebar } = useSidebar();

	const commitWidth = useCallback(
		(nextWidth: number) => {
			if (isCollapsed && nextWidth === width) return toggleSidebar();
			setLeftWidth(nextWidth);
		},
		[isCollapsed, width, toggleSidebar, setLeftWidth],
	);

	const resetWidth = useCallback(() => {
		setWidthVar(LEFT_NAV_DEFAULT_WIDTH);
		setLeftWidth(LEFT_NAV_DEFAULT_WIDTH);
	}, [setLeftWidth, setWidthVar]);

	return (
		<SidebarResizer
			enabled={isVisible}
			maxWidth={LEFT_NAV_MAX_WIDTH}
			minWidth={LEFT_NAV_MIN_WIDTH}
			onDoubleClick={resetWidth}
			onPointerEnter={onPointerEnter}
			onPointerLeave={onPointerLeave}
			onResize={onResize}
			onResizeStop={commitWidth}
			onResizingChange={onResizingChange}
			width={width}
			zIndex={NAV_Z_INDEX_VALUE}
			{...(isCollapsed && collapsedEdge(hasBottom))}
		/>
	);
};
